/**
 * @fileoverview Presentational body for the final self-assessment drawer:
 * project bar, the skills + contents score lists (hands picker) and the "My
 * notes" journal — in the same order/labels as the project's Arc assessment
 * drawer (ArcStudentAssessmentDrawerContent). The file-attachment section
 * was removed for Arc (AR-828). Fully controlled — the parent drawer owns
 * the scores/journal state and autosaves each change (there is no submit
 * button/footer).
 */

import { Group, Paper, Stack, Text, Textarea, Title } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import type { ScoreItem } from '../types'
import { AssessmentScoreItem } from './AssessmentScoreItem'

const JOURNAL_MIN_ROWS = 3

interface AssessmentDrawerBodyProps {
  projectName: string
  lesson?: string
  skills: readonly ScoreItem[]
  contents: readonly ScoreItem[]
  /** Controlled score-by-id map (0 = not scored). */
  scores: Readonly<Record<string, number>>
  journal: string
  onScoreChange: (id: string, score: number) => void
  onJournalChange: (journal: string) => void
}

export function AssessmentDrawerBody({
  projectName,
  lesson,
  skills,
  contents,
  scores,
  journal,
  onScoreChange,
  onJournalChange,
}: AssessmentDrawerBodyProps) {
  const { t } = useTranslation()
  const tr = (key: string) => t(`student_tasks.assessment_drawer.${key}`)

  const Section = ({ title, items, color }: { title: string; items: readonly ScoreItem[]; color: 'blue' | 'green' }) =>
    items.length === 0 ? null : (
      <Stack spacing="md">
        <Title order={5}>{title}</Title>
        {items.map(item => (
          <AssessmentScoreItem
            key={item.id}
            item={item}
            value={scores[item.id] ?? 0}
            onChange={score => onScoreChange(item.id, score)}
            color={color}
          />
        ))}
      </Stack>
    )

  return (
    <Stack spacing="lg">
      <Paper withBorder radius="sm" p="xs">
        <Stack spacing={2}>
          <Group spacing={4}>
            <Text size="sm" color="dimmed">{tr('project')}:</Text>
            <Text size="sm">{projectName}</Text>
          </Group>
          {lesson && (
            <Group spacing={4}>
              <Text size="sm" color="dimmed">{tr('lesson')}:</Text>
              <Text size="sm">{lesson}</Text>
            </Group>
          )}
        </Stack>
      </Paper>

      <Section title={tr('skills')} items={skills} color="blue" />
      <Section title={tr('standards')} items={contents} color="green" />

      {skills.length === 0 && contents.length === 0 && (
        <Text color="dimmed">{tr('no_items')}</Text>
      )}

      <Textarea
        label={tr('journal_label')}
        placeholder={tr('journal_placeholder')}
        value={journal}
        onChange={event => onJournalChange(event.currentTarget.value)}
        minRows={JOURNAL_MIN_ROWS}
        autosize
      />
    </Stack>
  )
}
